import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { courseRecordKeys, lessonLibraryKeys } from './courseRecordApi';
import { lessonKeys } from './courseApi';
import { fetchWithAuthRetry } from './apiUtils';
import type { ScormExportHistory } from '@course-authoring/shared-types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

export type AssignableContentType = 'lesson' | 'course';

export interface ClientAccount {
  id: string;
  name: string;
  externalId?: string;
  subdomain?: string;
  scormVersion?: string;
  apiKey?: string;
  integrationKey?: string;
  /**
   * True when the account has client-context customizations for the content a
   * scorm-context prefill was fetched for; drives per-account deploy builds.
   * Only populated on scorm-context account lists.
   */
  hasCustomizations?: boolean;
  /**
   * The account's OWN hoot program shell id (NA-361), populated on course
   * scorm-context account lists. Course account deploy targets must bake THIS
   * id — never the context's top-level (Standard) contentId — or learner
   * progress records against the wrong shell.
   */
  contentId?: string;
  /** Pair-scoped history returned by assignment reads; account lists omit it. */
  exportHistory?: ScormExportHistory;
}

export interface AccountAssignmentResult {
  accountIds: string[];
  /**
   * Server-side auto-deploy results for the newly assigned accounts (NA-210
   * copy of the published base packages). The panel consumes these instead of
   * re-deploying the same scopes — its own client-side build covers ONLY
   * accounts with customizations, which the server copy deliberately skips.
   * Absent when the content is Draft or the server predates the field.
   */
  deploys?: ServerAssignDeployResult[];
}

export type AssignmentPackageOutcomeStatus =
  | 'copied'
  | 'preserved_usable'
  | 'repair_required'
  | 'failed'
  | 'not_attempted';

export interface AssignmentPackageOutcome {
  contentPublicId: string;
  contentType: 'course' | 'lesson';
  title?: string;
  status: AssignmentPackageOutcomeStatus;
  reason?: string;
}

export interface ServerAssignDeployResult {
  accountSlug: string;
  success: boolean;
  error?: string;
  warning?: string;
  invalidation?: string;
  /**
   * The server deliberately withheld this scope's top-level package
   * (course-level customization needs an account-resolved build) — the panel
   * must deploy it client-side. This disposition, not the transitive
   * hasCustomizations flag, decides client-side builds: lesson-only
   * customization also flips that flag, but the server copy already handled
   * those scopes.
   */
  requiresClientDeploy?: boolean;
  /** Additive per-package copy results; absent on older assignment endpoints. */
  packageOutcomes?: AssignmentPackageOutcome[];
}

/**
 * Everything the SCORM export form can be prefilled with, sourced server-side:
 * contentId is the hoot lesson/program id (resolved via idempotent shell replay),
 * host is derived from the hoot base URL, and accounts are the content's
 * assigned accounts with their SCORM credentials.
 */
export interface ScormExportContext {
  contentId: string;
  authorAiCourseId?: string;
  contentType: 'microlessons' | 'programs';
  host: string;
  apiBaseUrl?: string;
  accounts: ClientAccount[];
  /**
   * True when the content's base (Emtrain) context — or a contained base
   * lesson — is in `Published — editing`. Assignment auto-deploy builds from
   * the draft layer, so while this is true it defers packaging rather than
   * ship the pending, unpublished edits to a newly assigned account.
   */
  hasPendingEdits?: boolean;
}

interface AccountAssignmentApiError {
  status: number;
  code: string;
  message: string;
  retryable: boolean;
}

export const accountAssignmentKeys = {
  all: ['accountAssignments'] as const,
  accounts: () => [...accountAssignmentKeys.all, 'accounts'] as const,
  contentScope: (contentType: AssignableContentType) =>
    [...accountAssignmentKeys.all, contentType] as const,
  assigned: (contentType: AssignableContentType, contentId: string) =>
    [...accountAssignmentKeys.all, contentType, contentId, 'assigned'] as const,
  scormContext: (contentType: AssignableContentType, contentId: string) =>
    [...accountAssignmentKeys.all, contentType, contentId, 'scormContext'] as const,
  assignedAccounts: (contentType: AssignableContentType, contentId: string) =>
    [...accountAssignmentKeys.all, contentType, contentId, 'assignedAccounts'] as const,
  accountsWithAssignments: (contentType: AssignableContentType) =>
    [...accountAssignmentKeys.all, 'accountsWithAssignments', contentType] as const,
  accountsWithQuestionnaires: () =>
    [...accountAssignmentKeys.all, 'accountsWithQuestionnaires'] as const,
};

function toApiError(response: Response, fallbackMessage: string): AccountAssignmentApiError {
  return {
    status: response.status,
    code: `HTTP_${response.status}`,
    message: response.statusText || fallbackMessage,
    retryable: response.status >= 500 || response.status === 429,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(record: Record<string, unknown>, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
  }
  return undefined;
}

function normalizeAccount(value: unknown): ClientAccount | null {
  if (!isRecord(value)) return null;

  const id = readString(value, ['externalAccountId', 'id', 'accountId', 'clientId', 'companyId']);
  if (!id) return null;

  const name = readString(value, [
    'name',
    'accountName',
    'clientName',
    'companyName',
    'title',
  ]) ?? id;

  return {
    id,
    name,
    // 'accountId' last: it's the hoot external account id on scorm-context
    // payloads (hoot's ScormAccount shape passed through author-api), but any
    // payload carrying an explicit external-id key should win over it.
    externalId: readString(value, ['externalAccountId', 'externalId', 'emtrainId', 'hootId', 'accountId']),
    subdomain: readString(value, ['subdomain', 'slug']),
    scormVersion: readString(value, ['scormVersion']),
    apiKey: readString(value, ['apiKey']),
    integrationKey: readString(value, ['integrationKey']),
    ...(typeof value.hasCustomizations === 'boolean'
      ? { hasCustomizations: value.hasCustomizations }
      : {}),
    // NA-361: the account's own program shell id from the course scorm context.
    // This normalizer rebuilds accounts field-by-field, so omitting it here
    // would silently drop the id and re-create the shared-shell defect.
    ...(readString(value, ['contentId']) !== undefined
      ? { contentId: readString(value, ['contentId']) }
      : {}),
  };
}

function normalizeExportHistory(value: unknown): ScormExportHistory {
  if (!isRecord(value)) return { hasBeenExported: true, firstExportedAt: null };

  const firstExportedAt = typeof value.firstExportedAt === 'string' && value.firstExportedAt.trim()
    ? value.firstExportedAt
    : null;
  if (firstExportedAt !== null && Number.isFinite(Date.parse(firstExportedAt))) {
    return { hasBeenExported: true, firstExportedAt };
  }
  if (value.firstExportedAt !== null && value.firstExportedAt !== undefined) {
    return { hasBeenExported: true, firstExportedAt: null };
  }
  if (value.hasBeenExported === false) return { hasBeenExported: false, firstExportedAt: null };
  return { hasBeenExported: true, firstExportedAt: null };
}

function unwrapListPayload(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  if (!isRecord(payload)) return [];

  const listKeys = ['assignments', 'accounts', 'clientAccounts', 'clients', 'companies', 'data', 'items'];
  for (const key of listKeys) {
    const value = payload[key];
    if (Array.isArray(value)) return value;
  }

  return [];
}

function normalizeAssignedIds(payload: unknown): string[] {
  const values = unwrapListPayload(payload);
  if (values.length === 0 && isRecord(payload)) {
    const accountIds = payload.assignedAccountIds ?? payload.accountIds;
    if (Array.isArray(accountIds)) {
      return accountIds
        .map((value) => typeof value === 'string' || typeof value === 'number' ? String(value) : null)
        .filter((value): value is string => Boolean(value));
    }
  }

  return values
    .map((value) => {
      if (typeof value === 'string' || typeof value === 'number') return String(value);
      return normalizeAccount(value)?.id ?? null;
    })
    .filter((value): value is string => Boolean(value));
}

function assignmentPath(contentType: AssignableContentType, contentId: string): string {
  const collection = contentType === 'lesson' ? 'lessons' : 'courses';
  return `${API_BASE_URL}/${collection}/${contentId}/assignments`;
}

function toExternalAccountId(accountId: string): string | number {
  return /^\d+$/.test(accountId) ? Number(accountId) : accountId;
}

export async function fetchAccounts(): Promise<ClientAccount[]> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/scorm-accounts`);
  if (!response.ok) throw toApiError(response, 'Failed to fetch accounts');

  const payload: unknown = await response.json();
  return unwrapListPayload(payload)
    .map(normalizeAccount)
    .filter((account): account is ClientAccount => account !== null)
    .sort((a, b) => a.name.localeCompare(b.name));
}

export async function fetchAssignedAccountIds(
  contentType: AssignableContentType,
  contentId: string
): Promise<string[]> {
  const response = await fetchWithAuthRetry(assignmentPath(contentType, contentId));
  if (!response.ok) throw toApiError(response, 'Failed to fetch assigned accounts');

  const payload: unknown = await response.json();
  return normalizeAssignedIds(payload);
}

export async function fetchAssignedAccounts(
  contentType: AssignableContentType,
  contentId: string
): Promise<ClientAccount[]> {
  const response = await fetchWithAuthRetry(assignmentPath(contentType, contentId));
  if (!response.ok) throw toApiError(response, 'Failed to fetch assigned accounts');

  const payload: unknown = await response.json();
  const assignments = unwrapListPayload(payload);
  return assignments
    .map((item): ClientAccount | null => {
      if (!isRecord(item)) return null;
      const account = normalizeAccount(item['account']);
      return account
        ? { ...account, exportHistory: normalizeExportHistory(item['exportHistory']) }
        : null;
    })
    .filter((account): account is ClientAccount => account !== null)
    .sort((a, b) => a.name.localeCompare(b.name));
}

export async function fetchAssignedAccountIdsForContentType(
  contentType: AssignableContentType
): Promise<string[]> {
  const response = await fetchWithAuthRetry(
    `${API_BASE_URL}/assignments/accounts?contentType=${contentType}`
  );
  if (!response.ok) throw toApiError(response, 'Failed to fetch assigned account ids for content type');

  const payload: unknown = await response.json();
  return normalizeAssignedIds(payload);
}

export async function fetchAccountsWithAssignments(
  contentType: AssignableContentType
): Promise<ClientAccount[]> {
  const [accounts, ids] = await Promise.all([
    fetchAccounts(),
    fetchAssignedAccountIdsForContentType(contentType),
  ]);
  const idSet = new Set(ids);
  return accounts.filter((a) => idSet.has(a.id));
}

/**
 * Accounts holding at least one Author-Platform questionnaire.
 *
 * Lives in this module rather than questionnaireApi because the answer is an
 * account list consumed by the context picker, and because the normalization it
 * needs (normalizeAssignedIds / normalizeAccount) is private here.
 */
export async function fetchAccountIdsWithQuestionnaires(): Promise<string[]> {
  const response = await fetchWithAuthRetry(`${API_BASE_URL}/questionnaires/accounts`);
  if (!response.ok) throw toApiError(response, 'Failed to fetch accounts with questionnaires');

  const payload: unknown = await response.json();
  return normalizeAssignedIds(payload);
}

export async function fetchAccountsWithQuestionnaires(): Promise<ClientAccount[]> {
  const [accounts, ids] = await Promise.all([
    fetchAccounts(),
    fetchAccountIdsWithQuestionnaires(),
  ]);
  const idSet = new Set(ids);
  return accounts.filter((a) => idSet.has(a.id));
}

export async function assignAccounts(input: {
  contentType: AssignableContentType;
  contentId: string;
  accountIds: string[];
}): Promise<AccountAssignmentResult> {
  const response = await fetchWithAuthRetry(assignmentPath(input.contentType, input.contentId), {
    method: 'POST',
    body: JSON.stringify({
      externalAccountId: input.accountIds.map(toExternalAccountId),
    }),
  });

  if (!response.ok) throw toApiError(response, 'Failed to assign accounts');

  const payload: unknown = await response.json().catch(() => null);
  if (payload === null) {
    return { accountIds: input.accountIds };
  }

  return {
    accountIds: normalizeAssignedIds(payload),
    ...(isRecord(payload) && Array.isArray(payload.deploys)
      ? { deploys: normalizeAssignDeploys(payload.deploys) }
      : {}),
  };
}

function normalizeAssignDeploys(raw: unknown[]): ServerAssignDeployResult[] {
  const deploys: ServerAssignDeployResult[] = [];
  for (const entry of raw) {
    if (!isRecord(entry) || typeof entry.accountSlug !== 'string') continue;
    deploys.push({
      accountSlug: entry.accountSlug,
      success: entry.success === true,
      ...(typeof entry.error === 'string' ? { error: entry.error } : {}),
      ...(typeof entry.warning === 'string' ? { warning: entry.warning } : {}),
      ...(typeof entry.invalidation === 'string' ? { invalidation: entry.invalidation } : {}),
      ...(typeof entry.requiresClientDeploy === 'boolean' ? { requiresClientDeploy: entry.requiresClientDeploy } : {}),
      ...(Array.isArray(entry.packageOutcomes)
        ? { packageOutcomes: normalizeAssignmentPackageOutcomes(entry.packageOutcomes) }
        : {}),
    });
  }
  return deploys;
}

const assignmentPackageOutcomeStatuses = new Set<AssignmentPackageOutcomeStatus>([
  'copied',
  'preserved_usable',
  'repair_required',
  'failed',
  'not_attempted',
]);

function normalizeAssignmentPackageOutcomes(raw: unknown[]): AssignmentPackageOutcome[] {
  const outcomes: AssignmentPackageOutcome[] = [];
  for (const entry of raw) {
    if (
      !isRecord(entry)
      || typeof entry.contentPublicId !== 'string'
      || entry.contentPublicId.trim().length === 0
      || (entry.contentType !== 'course' && entry.contentType !== 'lesson')
      || typeof entry.status !== 'string'
      || !assignmentPackageOutcomeStatuses.has(entry.status as AssignmentPackageOutcomeStatus)
    ) continue;
    const title = typeof entry.title === 'string' && entry.title.trim() ? entry.title.trim() : undefined;
    const reason = typeof entry.reason === 'string' && entry.reason.trim() ? entry.reason.trim() : undefined;
    outcomes.push({
      contentPublicId: entry.contentPublicId.trim(),
      contentType: entry.contentType,
      status: entry.status as AssignmentPackageOutcomeStatus,
      ...(title ? { title } : {}),
      ...(reason ? { reason } : {}),
    });
  }
  return outcomes;
}

export async function fetchScormExportContext(
  contentType: AssignableContentType,
  contentId: string
): Promise<ScormExportContext> {
  const collection = contentType === 'lesson' ? 'lessons' : 'courses';
  const response = await fetchWithAuthRetry(
    `${API_BASE_URL}/${collection}/${contentId}/scorm-context`
  );
  if (!response.ok) throw toApiError(response, 'Failed to fetch SCORM export context');

  const payload: unknown = await response.json();
  if (!isRecord(payload)) throw toApiError(response, 'Malformed SCORM export context');

  const rawAccounts = Array.isArray(payload.accounts) ? payload.accounts : [];
  return {
    contentId: readString(payload, ['contentId']) ?? '',
    authorAiCourseId: readString(payload, ['authorAiCourseId']),
    contentType: payload.contentType === 'programs' ? 'programs' : 'microlessons',
    host: readString(payload, ['host']) ?? '',
    apiBaseUrl: readString(payload, ['apiBaseUrl']),
    accounts: rawAccounts
      .map(normalizeAccount)
      .filter((account): account is ClientAccount => account !== null),
    hasPendingEdits: payload.hasPendingEdits === true,
  };
}

export function useScormExportContext(
  contentType: AssignableContentType,
  contentId: string | null
) {
  return useQuery({
    queryKey: accountAssignmentKeys.scormContext(contentType, contentId ?? ''),
    queryFn: () => fetchScormExportContext(contentType, contentId ?? ''),
    enabled: Boolean(contentId),
    // Always refetch when the export dialog opens (enabled flips with it):
    // per-account deploys partition on each account's hasCustomizations flag,
    // and a stale flag would silently ship the base package to an account
    // customized since the last fetch. Cached data still renders immediately.
    staleTime: 0,
  });
}

export function useClientAccounts() {
  return useQuery({
    queryKey: accountAssignmentKeys.accounts(),
    queryFn: fetchAccounts,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAssignedAccountIds(
  contentType: AssignableContentType,
  contentId: string | null
) {
  return useQuery({
    queryKey: accountAssignmentKeys.assigned(contentType, contentId ?? ''),
    queryFn: () => fetchAssignedAccountIds(contentType, contentId ?? ''),
    enabled: Boolean(contentId),
  });
}

export function useAssignAccounts(options?: {
  onSuccess?: (data: AccountAssignmentResult) => void;
  onError?: (error: AccountAssignmentApiError) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: assignAccounts,
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        accountAssignmentKeys.assigned(variables.contentType, variables.contentId),
        data.accountIds
      );
      queryClient.invalidateQueries({
        queryKey: accountAssignmentKeys.assignedAccounts(variables.contentType, variables.contentId),
      });
      queryClient.invalidateQueries({
        queryKey: accountAssignmentKeys.accountsWithAssignments(variables.contentType),
      });
      queryClient.invalidateQueries({ queryKey: lessonKeys.lists() });
      queryClient.invalidateQueries({ queryKey: lessonLibraryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: courseRecordKeys.lists() });
      // NA-283: assignment count gates Delete on published content, so the
      // open editor's detail read (every locale/context variant of this item)
      // must refresh or its top-bar Delete keeps the pre-assignment state.
      const detailPrefix = variables.contentType === 'course'
        ? [...courseRecordKeys.details(), variables.contentId]
        : [...lessonKeys.details(), variables.contentId];
      queryClient.invalidateQueries({ queryKey: detailPrefix });
      if (variables.contentType === 'course') {
        // Assigning a course cascades lesson assignments server-side (NA-195):
        // drop every lesson-scoped assignment cache so contained lessons offer
        // the new client context without waiting out staleTime or a reload.
        queryClient.invalidateQueries({ queryKey: accountAssignmentKeys.contentScope('lesson') });
        queryClient.invalidateQueries({ queryKey: accountAssignmentKeys.accountsWithAssignments('lesson') });
      }
      options?.onSuccess?.(data);
    },
    onError: (error: unknown) => {
      if (error && typeof error === 'object' && 'status' in error) {
        options?.onError?.(error as AccountAssignmentApiError);
      }
    },
  });
}
